import debounce from 'lodash/debounce'
import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { Fields, IOption, IWhere } from 'types/common'
import { IFilter } from 'types/common/query'
import {
  getCollection,
  getCollectionsByFilter,
  getCollectionsByFilterWithRelations,
  getCollectionsByGroups,
  upsertCollectionTags
} from 'API/collection'
import { getGroups } from 'API/groups'
import { searchGlobal } from 'API/search'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { AuthRoleIdEnum } from 'constants/user'
import { ICollection, ICollectionFilter, ICollectionsProcess } from 'interfaces/collection'
import { IGroup, IGroupMemberPermissionId } from 'interfaces/group'
import { GlobalSearchDto, ISearchFilterForm } from 'stores/searchStore'
import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { getValidArray } from 'utils/common'

// A Stack counts as part of a collection only while it is published and not
// archived. One predicate, so the detail list and its count cannot disagree.
const isValidCollectionsProcess = (collectionProcess?: ICollectionsProcess): boolean =>
  !!collectionProcess?.process &&
  !collectionProcess.process.archivedAt &&
  collectionProcess.process.isPublished !== false

const EMPTY_COLLECTION_PROCESS_FILTER: ISearchFilterForm = { tags: [], creators: [] }

// Must match the `pageSizeKey` / options the Collection Detail pager is given.
export const COLLECTION_PROCESS_PAGE_SIZE_KEY = 'collectionProcessPageSize'
export const COLLECTION_PROCESS_PAGE_SIZE_OPTIONS = [10, 20, 40, 100]

// Read the remembered rows-per-page up front. Left to the pager's mount effect,
// the list first rendered 10 rows and then jumped to the saved size.
function readStoredCollectionProcessPageSize(): number {
  try {
    const stored = Number(localStorage.getItem(COLLECTION_PROCESS_PAGE_SIZE_KEY))
    return COLLECTION_PROCESS_PAGE_SIZE_OPTIONS.includes(stored) ? stored : COLLECTION_PROCESS_PAGE_SIZE_OPTIONS[0]
  } catch {
    return COLLECTION_PROCESS_PAGE_SIZE_OPTIONS[0]
  }
}

class CollectionStore {
  rootStore: RootStore

  isSearching: boolean | null = null
  isLoading = false
  isEditing = false
  isManageMode = false
  isManagePermission = false
  isOpenDiscardModal = false

  collections: ICollection[] = []
  selectedCollectionId: number | null = null
  totalCollections: number = 0
  size: number = 8
  from: number = 0

  overviewCollection: ICollection | null = null
  collection: ICollection | null = null

  groupFilterOptions: IGroup[] = []
  lastFilter: ICollectionFilter | null = null

  // Stack list on Collection Detail. With no search keyword or filter the page
  // pages `collection.collectionsProcesses` itself; Elasticsearch is only asked
  // when the coach searches or filters, and then it pages server-side.
  collectionProcessSearchResults: IEsGlobalSearchItem[] = []
  collectionProcessTotalResults: number = 0
  isSearchingCollectionProcesses: boolean | null = null
  collectionProcessSearchText: string = ''
  collectionProcessSearchFilter: ISearchFilterForm = { ...EMPTY_COLLECTION_PROCESS_FILTER }
  collectionProcessIds: number[] = []
  collectionProcessCacheFilter: IFilter<GlobalSearchDto> = {}
  collectionProcessPage: number = 1
  collectionProcessPageSize: number = readStoredCollectionProcessPageSize()

  debouncedSearchCollectionProcesses: () => void

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)

    this.rootStore = rootStore
    this.debouncedSearchCollectionProcesses = debounce(() => this.searchCollectionProcesses(), 700)
  }

  public changePage(page: number, fetchFunction: () => Promise<void>): void {
    this.from = (page - 1) * this.size
    fetchFunction()
  }

  public changeSize = (pageIndex: number = 8): void => {
    this.size = Number(pageIndex)
  }

  public async getOverviewCollection(collectionId: number): Promise<ICollection> {
    this.selectedCollectionId = collectionId
    const collection: ICollection = await getCollection(collectionId, {
      include: [
        {
          relation: 'collectionsProcesses',
          scope: {
            fields: ['processId', 'collectionId'],
            include: [
              {
                relation: 'process',
                scope: {
                  where: {
                    archivedAt: { inq: [undefined, null] }
                  },
                  fields: ['id', 'image', 'name', 'procedureIcon', 'archivedAt'],
                  include: [
                    {
                      relation: 'steps',
                      scope: {
                        fields: ['id', 'processId'],
                        include: [
                          {
                            relation: 'media',
                            scope: {
                              fields: ['id', 'image', 'stepId', 'mediaType', 'video'],
                              where: {
                                mediaType: { inq: ['image', 'video'] }
                              },
                              order: ['position ASC']
                            }
                          }
                        ],
                        order: ['position ASC']
                      }
                    }
                  ]
                }
              }
            ],
            order: ['position ASC']
          }
        },
        {
          relation: 'groups'
        },
        {
          relation: 'tags'
        }
      ]
    })
    const collectionsProcesses: ICollectionsProcess[] = getValidArray(collection?.collectionsProcesses).filter(
      collectionProcess => collectionProcess?.process
    )

    this.overviewCollection = { ...collection, collectionsProcesses }

    return collection
  }

  public async getCollectDetail(collectionId: number): Promise<ICollection> {
    this.selectedCollectionId = collectionId
    const collection: ICollection = await getCollection(collectionId, {
      include: [
        {
          relation: 'collectionsProcesses',
          scope: {
            fields: ['processId', 'collectionId'],
            include: [
              {
                relation: 'process',
                scope: {
                  fields: [
                    'id',
                    'image',
                    'name',
                    'procedureIcon',
                    'archivedAt',
                    'createdBy',
                    'isPublished',
                    'organizationId',
                    'mainMedia'
                  ],
                  include: [
                    {
                      relation: 'steps',
                      scope: {
                        fields: ['id', 'processId', 'name', 'position'],
                        include: [
                          {
                            relation: 'media',
                            scope: {
                              fields: ['id', 'image', 'stepId', 'mediaType', 'video'],
                              where: {
                                mediaType: { inq: ['image', 'video'] }
                              },
                              order: ['position ASC']
                            }
                          }
                        ],
                        order: ['position ASC']
                      }
                    },
                    {
                      relation: 'tags'
                    },
                    {
                      relation: 'creator',
                      scope: {
                        fields: ['id', 'firstName', 'lastName', 'image']
                      }
                    }
                  ]
                }
              }
            ],
            order: ['position ASC']
          }
        },
        {
          relation: 'userCollections'
        },
        {
          relation: 'groups'
        },
        {
          relation: 'tags'
        }
      ]
    })

    const groupIdsOfCollection = getValidArray(collection?.groups).map(group => group?.id)
    const editorGroupIdsOfUser = getValidArray(this.rootStore.userStore.currentUserGroupMembers)
      .filter(groupMember => groupMember?.permissionId !== IGroupMemberPermissionId.VIEWER)
      .map(groupMember => groupMember?.groupId)

    const isManagePermission = groupIdsOfCollection?.some(groupId => editorGroupIdsOfUser?.includes(groupId ?? 0))

    this.isManagePermission = isManagePermission

    this.collection = {
      ...collection,
      collectionsProcesses: getValidArray(collection?.collectionsProcesses).filter(isValidCollectionsProcess)
    }
    return collection
  }

  public async updateCollectionTags(collectionId: number, tagIds: number[]): Promise<void> {
    await upsertCollectionTags(collectionId, tagIds)
    if (collectionId === this.collection?.id) {
      this.collection = {
        ...this.collection,
        tagIds,
        tags: getValidArray(this.collection?.tags).filter(tag => tagIds.includes(tag.id ?? 0))
      }
      const missingTagIds = tagIds.filter(tagId => !getValidArray(this.collection?.tags).some(tag => tag.id === tagId))
      if (missingTagIds.length > 0) {
        const refreshed = await this.getCollectDetail(collectionId)
        this.collection = refreshed
      }
    }
  }

  public async fetchCollectionsByFilter(filter: ICollectionFilter, useLastFilter: boolean = false): Promise<void> {
    this.isLoading = true
    const apiFilter = useLastFilter && this.lastFilter ? { ...this.lastFilter, skip: 0 } : filter
    const response = await getCollectionsByFilter(apiFilter)
    this.collections = response.paginatedResults ?? []
    this.totalCollections = response?.totalResults ?? 0
    if (!useLastFilter) {
      this.lastFilter = filter
    }
    this.isLoading = false
  }

  public async fetchCollectionsByFilterWithRelations(
    filter: ICollectionFilter,
    useLastFilter: boolean = false
  ): Promise<void> {
    this.isLoading = true
    const apiFilter = useLastFilter && this.lastFilter ? { ...this.lastFilter, skip: 0 } : filter
    const response = await getCollectionsByFilterWithRelations(apiFilter)
    this.collections = response.paginatedResults ?? []
    this.totalCollections = response?.totalResults ?? 0
    if (!useLastFilter) {
      this.lastFilter = filter
    }
    this.isLoading = false
  }

  public async fetchCollectionsByGroup(filter: {
    where: IWhere<ICollection | any>
    groups?: number[] | string[]
    limit?: number
    offset?: number
    fields?: Fields<ICollection>
    order?: string[]
  }): Promise<{ result: ICollection[]; totalCount: number }> {
    this.isSearching = true
    const response = await getCollectionsByGroups(filter)
    this.collections = response.result
    this.totalCollections = response.totalCount
    this.isSearching = false
    return response
  }

  public async fetchGroupOptions(): Promise<IOption<number>[]> {
    const isBasicUser = this.rootStore.authStore.currentMembership?.roleId === AuthRoleIdEnum.PLAYER
    const groupOptions: IOption<number>[] = []
    if (isBasicUser) {
      const currentUserGroupMembers = this?.rootStore?.userStore?.currentUserGroupMembers ?? []
      const groupIdsUserIsMember = currentUserGroupMembers.map(groupMember => groupMember.groupId)
      const basicUserQuery = {
        where: {
          id: {
            inq: groupIdsUserIsMember
          }
        },
        fields: ['id', 'name']
      }
      const groups = await getGroups(basicUserQuery)
      this.groupFilterOptions = groups
      groupOptions.push(
        ...[
          ...groups.map(group => ({
            label: group?.name ?? '',
            value: Number(group?.id)
          }))
        ]
      )
    } else {
      const organizationId = this.rootStore.authStore.activeOrgId ?? 0
      const adminQuery = {
        where: {
          organizationId
        },
        fields: ['id', 'name']
      }
      const groups = await getGroups(adminQuery)
      this.groupFilterOptions = groups
      groupOptions.push(
        ...[
          ...groups.map(group => ({
            label: group?.name ?? '',
            value: Number(group?.id)
          }))
        ]
      )
    }
    return groupOptions
  }

  public async fetchGroupsWithEditorPermissions(): Promise<IOption<number>[]> {
    const isBasicUser = this.rootStore.authStore.currentMembership?.roleId === AuthRoleIdEnum.PLAYER
    const groupOptions: IOption<number>[] = []

    if (isBasicUser) {
      // For basic users, only get groups where they have editor permissions
      const currentUserGroupMembers = this?.rootStore?.userStore?.currentUserGroupMembers ?? []
      const editorGroupIds = currentUserGroupMembers
        .filter(groupMember => groupMember?.permissionId === IGroupMemberPermissionId.EDITOR)
        .map(groupMember => groupMember.groupId)

      if (editorGroupIds.length > 0) {
        const basicUserQuery = {
          where: {
            id: {
              inq: editorGroupIds
            }
          },
          fields: ['id', 'name']
        }
        const groups = await getGroups(basicUserQuery)
        groupOptions.push(
          ...groups.map(group => ({
            label: group?.name ?? '',
            value: Number(group?.id)
          }))
        )
      }
    } else {
      // For admins, get all groups in organization
      const organizationId = this.rootStore.authStore.activeOrgId ?? 0
      const adminQuery = {
        where: {
          organizationId
        },
        fields: ['id', 'name']
      }
      const groups = await getGroups(adminQuery)
      groupOptions.push(
        ...groups.map(group => ({
          label: group?.name ?? '',
          value: Number(group?.id)
        }))
      )
    }
    return groupOptions
  }

  public async fetchCollectionsWithEditorPermissions(): Promise<IOption<number>[]> {
    const isBasicUser = this.rootStore.authStore.currentMembership?.roleId === AuthRoleIdEnum.PLAYER
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0

    if (isBasicUser) {
      // For basic users, get collections where they have editor permissions through groups
      const currentUserGroupMembers = this?.rootStore?.userStore?.currentUserGroupMembers ?? []
      const editorGroupIds = currentUserGroupMembers
        .filter(groupMember => groupMember?.permissionId === IGroupMemberPermissionId.EDITOR)
        .map(groupMember => groupMember.groupId)

      if (editorGroupIds.length === 0) {
        return []
      }

      const filter = {
        where: {
          organizationId,
          $or: [{ archivedAt: { $exists: false } }, { archivedAt: { $eq: null } }]
        },
        include: [
          {
            relation: 'groups',
            scope: {
              where: {
                id: { inq: editorGroupIds }
              }
            }
          }
        ],
        fields: { id: true, name: true }
      }

      const response = await getCollectionsByGroups(filter)
      const collections = response.result

      return collections.map(collection => ({
        label: collection?.name ?? '',
        value: Number(collection?.id)
      }))
    }

    // For admins, get all collections in organization
    const filter = {
      where: {
        organizationId,
        $or: [{ archivedAt: { $exists: false } }, { archivedAt: { $eq: null } }]
      },
      fields: { id: true, name: true }
    }

    const response = await getCollectionsByGroups(filter)
    return response.result.map(collection => ({
      label: collection?.name ?? '',
      value: Number(collection?.id)
    }))
  }

  public setCollections(collections: ICollection[], count: number) {
    this.collections = collections
    this.totalCollections = count
  }

  public setIsEditing = (isEditing: boolean): void => {
    this.isEditing = isEditing
  }

  public setIsOpenDiscardModal = (isOpenDiscardModal: boolean): void => {
    this.isOpenDiscardModal = isOpenDiscardModal
  }

  public setManageMode = (isManageMode: boolean, isCollectionDetailPage?: boolean): void => {
    if (isCollectionDetailPage && this.isManageMode && this.isEditing) {
      this.setIsOpenDiscardModal(true)
      return
    }
    this.isManageMode = isManageMode
  }

  public setIsManagePermission = (isManagePermission: boolean): void => {
    this.isManagePermission = isManagePermission
  }

  public resetCollection = (): void => {
    this.collection = null
    this.overviewCollection = null
  }

  /**
   * Clear org-scoped collection state on active-profile switch so a switched
   * profile never inherits another profile's cached collection list/detail.
   */
  public reset = (): void => {
    this.resetCollection()
    this.collections = []
    this.selectedCollectionId = null
    this.totalCollections = 0
    this.from = 0
    this.groupFilterOptions = []
    this.lastFilter = null
    this.isSearching = null
    this.isLoading = false
    this.isEditing = false
    this.isManageMode = false
    this.isManagePermission = false
    this.isOpenDiscardModal = false
    this.clearCollectionProcessSearch()
  }

  public changeCollectionProcessIds(processIds: number[]): void {
    this.collectionProcessIds = processIds
  }

  public changeCollectionProcessSearchText(searchText: string): void {
    this.collectionProcessSearchText = searchText
    this.collectionProcessPage = 1
    // Without a keyword or filter the page lists `collectionsProcesses` directly.
    if (searchText.trim() || this.isCollectionProcessFilterActive) {
      // Flag the search now, not after the debounce: until it lands the page shows
      // "Searching..." instead of the previous result set.
      this.isSearchingCollectionProcesses = true
      this.debouncedSearchCollectionProcesses()
    }
  }

  get isCollectionProcessFilterActive(): boolean {
    return (
      getValidArray(this.collectionProcessSearchFilter.tags).length > 0 ||
      getValidArray(this.collectionProcessSearchFilter.creators).length > 0
    )
  }

  public changeCollectionProcessSearchFilter(filter: ISearchFilterForm): void {
    this.collectionProcessSearchFilter = filter
    this.collectionProcessPage = 1
  }

  public changeCollectionProcessPage(page: number): void {
    this.collectionProcessPage = page
    this.searchCollectionProcesses()
  }

  public changeCollectionProcessPageSize(size: number): void {
    if (this.collectionProcessPageSize === Number(size)) return
    this.collectionProcessPageSize = Number(size)
    this.collectionProcessPage = 1
    this.searchCollectionProcesses()
  }

  // Silent variants: move the pager without an Elasticsearch call, for when the
  // page slices `collection.collectionsProcesses` itself.
  public setCollectionProcessPage(page: number): void {
    this.collectionProcessPage = page
  }

  public setCollectionProcessPageSize(size: number): void {
    if (this.collectionProcessPageSize === Number(size)) return
    this.collectionProcessPageSize = Number(size)
    this.collectionProcessPage = 1
  }

  public async searchCollectionProcesses(): Promise<void> {
    if (this.collectionProcessIds.length === 0) {
      // Nothing to search, but clear the flag a keystroke may have raised.
      this.isSearchingCollectionProcesses = false
      return
    }

    try {
      this.isSearchingCollectionProcesses = true

      const filter: IFilter<GlobalSearchDto> = {
        where: {
          searchText: this.collectionProcessSearchText ?? '',
          isPublished: true,
          collectionName: EEsGlobalSearchCollection.PROCESS,
          processIds: this.collectionProcessIds
        },
        skip: (this.collectionProcessPage - 1) * this.collectionProcessPageSize,
        limit: this.collectionProcessPageSize
      }

      if (getValidArray(this.collectionProcessSearchFilter.tags).length > 0) {
        filter.where!.tagIds = getValidArray(this.collectionProcessSearchFilter.tags).map(tag => Number(tag.value))
      }

      if (getValidArray(this.collectionProcessSearchFilter.creators).length > 0) {
        filter.where!.creatorIds = getValidArray(this.collectionProcessSearchFilter.creators).map(c => Number(c.value))
      }

      if (
        JSON.stringify(this.collectionProcessCacheFilter) === JSON.stringify(filter) &&
        this.collectionProcessSearchResults.length > 0
      ) {
        return
      }
      this.collectionProcessCacheFilter = filter

      const result = await searchGlobal(filter)
      this.collectionProcessSearchResults = result.data
      this.collectionProcessTotalResults = result.total
    } catch (error: unknown) {
      console.error('CollectionStore ~ searchCollectionProcesses ~ error', error instanceof Error ? error.message : error)
    } finally {
      this.isSearchingCollectionProcesses = false
    }
  }

  public clearCollectionProcessSearch(): void {
    this.collectionProcessSearchResults = []
    this.collectionProcessTotalResults = 0
    this.isSearchingCollectionProcesses = null
    this.collectionProcessSearchText = ''
    this.collectionProcessSearchFilter = { ...EMPTY_COLLECTION_PROCESS_FILTER }
    this.collectionProcessIds = []
    this.collectionProcessCacheFilter = {}
    this.collectionProcessPage = 1
  }
}

export default CollectionStore
